import { Card, Segmented, Space, Spin, Typography } from 'antd';
import { CheckCircleFilled, DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useThemes } from '@presentation/hooks/useThemes';
import { useUpdateSetting } from '@presentation/hooks/useSettings';
import { useThemeStore, type ThemePreference } from '@presentation/stores/themeStore';

const APPEARANCE_OPTIONS: { label: string; value: ThemePreference; icon: React.ReactNode }[] = [
  { label: 'Light', value: 'light', icon: <SunOutlined /> },
  { label: 'Dark', value: 'dark', icon: <MoonOutlined /> },
  { label: 'System', value: 'system', icon: <DesktopOutlined /> },
];

export const PreferencesTab = () => {
  const { data: themes, isLoading } = useThemes();
  const updateSetting = useUpdateSetting();
  const themeSlug = useThemeStore((state) => state.themeSlug);
  const setTheme = useThemeStore((state) => state.setTheme);
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);

  if (isLoading) {
    return <Spin />;
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <Typography.Title level={5}>Appearance</Typography.Title>
        <Segmented
          value={preference}
          onChange={(value) => setPreference(value as ThemePreference)}
          options={APPEARANCE_OPTIONS.map((option) => ({
            label: (
              <span className="flex items-center gap-1.5 px-1">
                {option.icon} {option.label}
              </span>
            ),
            value: option.value,
          }))}
        />
      </div>

      <Typography.Title level={5}>Theme</Typography.Title>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {themes?.map((theme) => (
          <Card
            key={theme.id}
            hoverable
            size="small"
            onClick={() => {
              setTheme(theme.slug, theme.mode);
              updateSetting.mutate({ key: 'theme', value: theme.slug });
            }}
            style={{
              backgroundColor: theme.colors.background,
              color: theme.colors.foreground,
              borderColor: themeSlug === theme.slug ? theme.colors.accent : undefined,
              borderWidth: themeSlug === theme.slug ? 2 : 1,
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{theme.name}</span>
              {themeSlug === theme.slug && (
                <CheckCircleFilled style={{ color: theme.colors.accent }} />
              )}
            </div>
            <Space size={6} className="mt-2">
              {Object.values(theme.colors)
                .slice(0, 4)
                .map((color, index) => (
                  <span
                    key={index}
                    className="inline-block h-4 w-4 rounded-full border border-black/10"
                    style={{ backgroundColor: color }}
                  />
                ))}
            </Space>
          </Card>
        ))}
      </div>
    </div>
  );
};
